import { useEffect, useState } from 'react'
import { api, type BillingStatus } from '../api'
import { radius, theme } from '../theme'
import { Button, Card, Page } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Mascot } from '../components/Mascot'
import { fromServer, t } from '../lib/i18n'

/**
 * The one screen between a new family and the app, once sign-up is open.
 *
 * Only ever seen by a grown-up: it sits after sign-in and the agreement, and
 * before any child is added. Never shown to the allow list, and never while
 * sign-up is closed — the server's gates decide that, not this screen.
 *
 * **Coming back from Stripe proves nothing.** The return address only tells
 * this screen to wait; what actually opens the app is Stripe's signed webhook
 * reaching the server. So after paying this polls quietly until the server
 * agrees, rather than letting the address in on its own say-so.
 */
export function Subscribe({ onDone, onSignOut }: { onDone: () => void; onSignOut: () => void }) {
  const [b, setB] = useState<BillingStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // A failed load must say so. The first version caught the error and set
  // nothing, so the screen sat on "Loading" for ever.
  const [loadError, setLoadError] = useState<unknown>(null)
  const returned = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('billing') : null

  const load = () => {
    setLoadError(null)
    api.billing().then(setB).catch(setLoadError)
  }
  useEffect(load, [])

  // Back from Stripe: wait for the webhook to land, then carry on.
  useEffect(() => {
    if (returned !== 'done') return
    let tries = 0
    const timer = window.setInterval(async () => {
      tries += 1
      try {
        const state = await api.state()
        if (state.next_screen !== 'subscribe' || tries > 30) {
          window.clearInterval(timer)
          window.history.replaceState(null, '', window.location.pathname)
          onDone()
        }
      } catch { /* keep waiting */ }
    }, 1500)
    return () => window.clearInterval(timer)
  }, [returned, onDone])

  if (returned === 'done') return <Loading what={t('Finishing up — this takes a moment')} grownUp />
  if (loadError) return <ErrorScreen error={loadError} onRetry={load} grownUp />
  if (!b) return <Loading grownUp />

  const start = async () => {
    setBusy(true)
    setProblem(null)
    try {
      const { url } = await api.billingCheckout()
      window.location.assign(url)
    } catch (err) {
      setProblem(fromServer(err, 'That did not work. Try again.'))
      setBusy(false)
    }
  }

  const price = b.price_label || t('the monthly price')
  return (
    <Page width={480}>
      <div style={{ display: 'grid', placeItems: 'center', padding: '28px 0 8px' }}>
        <Mascot size={110} mood="cheer" />
      </div>
      <h1 style={{ margin: '6px 0 6px', fontSize: 32, textAlign: 'center', color: theme.greenDarker }}>
        {t('Start your free trial')}
      </h1>
      <p style={{ margin: '0 0 18px', fontSize: 18, textAlign: 'center', color: theme.text2 }}>
        {t('{n} days free, then {price}.', { n: b.trial_days, price })}
      </p>

      <Card>
        <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: theme.text2, fontSize: 16, lineHeight: 1.7 }}>
          <li>{t('Every child in your family, one price.')}</li>
          <li>{t('Progress emails, the word universe, the trophy room — all of it.')}</li>
          <li>{t('No ads, and nothing to buy inside the app.')}</li>
          <li>{t('Cancel any time, in two taps, from Settings.')}</li>
        </ul>
        {b.ready ? (
          <>
            <Button full onClick={start} disabled={busy}>
              {busy ? t('Opening the secure payment page…') : t('Start {n} days free', { n: b.trial_days })}
            </Button>
            <p style={{ margin: '12px 0 0', fontSize: 14, color: theme.muted, lineHeight: 1.5 }}>
              {b.needs_card
                ? t("You'll add a card on Stripe's secure page. Nothing is charged until the {n} days are up, and you can cancel before then.", { n: b.trial_days })
                : t('No card needed to start. At the end of the trial you can add one to carry on.')}
              {' '}{t('Your card goes to Stripe, never to Word Sprout.')}
            </p>
          </>
        ) : (
          <p style={{
            margin: 0, padding: 12, borderRadius: radius.md, background: theme.orangePale,
            color: theme.orangeDark, fontSize: 15, lineHeight: 1.5,
          }}>
            {t("Payments aren't switched on yet, so a trial can't start. Please try again soon.")}
          </p>
        )}
        {returned === 'cancelled' ? (
          <p style={{ margin: '12px 0 0', fontSize: 14, color: theme.text2 }}>
            {t("No problem — nothing was charged. Start whenever you're ready.")}
          </p>
        ) : null}
        {problem ? <p style={{ margin: '12px 0 0', color: theme.red, fontSize: 15 }}>{problem}</p> : null}
      </Card>

      <div style={{ textAlign: 'center', marginTop: 18 }}>
        <button type="button" onClick={onSignOut} style={{
          background: 'transparent', border: 'none', color: theme.muted, fontWeight: 700,
          fontSize: 15, cursor: 'pointer', minHeight: 44,
        }}>{t('Sign out')}</button>
      </div>
    </Page>
  )
}
